import { useState } from 'react';
import { X } from 'lucide-react';

import { useCart } from '@/features/marketplace/stores/cart';

/**
 * SearchBar — top of every authed page.
 *
 * Two-cell layout inside a 0.2rem-padded outer wrapper: a light gray
 * search field on the left, a darker gray cart counter on the right.
 * The padding creates the thin frame visible between/around the cells.
 * Wrapper bg matches the counter cell so the right edge reads as
 * contiguous, while the input cell pops against it.
 */
export function SearchBar({
  value,
  onChange,
  onSubmit,
  onCartClick,
}: {
  value?: string;
  onChange?: (v: string) => void;
  onSubmit?: (v: string) => void;
  onCartClick?: () => void;
}) {
  const [internal, setInternal] = useState(value ?? '');
  const cartCount = useCart((s) => s.items.length);
  const v = value !== undefined ? value : internal;
  const set = (next: string) => {
    if (value === undefined) setInternal(next);
    onChange?.(next);
  };

  return (
    <div className="w-full p-2 flex gap-2 bg-[#F5F5F5] sticky top-0 z-10">
      <form
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit?.(v);
        }}
        className="flex-1 min-w-0 flex bg-[#ECECEC]"
      >
        <input
          type="text"
          value={v}
          onChange={(e) => set(e.target.value)}
          placeholder="Search for something..."
          aria-label="Search"
          className="flex-1 min-w-0 h-40 px-15 bg-transparent font-body text-15 text-black placeholder:text-[#7C7C7C] outline-none border-0 focus:outline-none focus:ring-0"
        />
        {v && (
          <button
            type="button"
            onClick={() => set('')}
            aria-label="Clear search"
            className="w-40 h-40 shrink-0 flex items-center justify-center text-[#7C7C7C] hover:text-black"
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        )}
      </form>
      <button
        type="button"
        onClick={onCartClick}
        aria-label={
          cartCount === 0
            ? 'Cart, empty'
            : `Cart, ${cartCount} item${cartCount === 1 ? '' : 's'}`
        }
        className="w-40 h-40 shrink-0 flex items-center justify-center bg-slate font-mono text-15 text-black hover:bg-[#CCCCCC]"
      >
        {cartCount}
      </button>
    </div>
  );
}
